'use strict'

/**
 * Catalog on-hand helpers for upstream melt picker.
 *
 * Live Product rows currently expose aggregate `qtyOnHand` only.
 * `withPrototypeOnHandByLocation` synthesizes a stable CO/NY split at the
 * search seam so the picker can demo location chrome until `/products`
 * returns real `onHandByLocation[]`.
 *
 * Lot inventory location is a rich `{locationCode, netsuiteInternalId}` —
 * convert string codes at the door via `normalizeLotInventoryLocation`.
 * Export never invents NetSuite ids from bare codes.
 */

import {WarehouseNetsuiteIds, Warehouses} from '@/util/constants'

/** Canonical CO/NY lot governors (convert-at-door table; not an export-time lookup). */
export const LOT_INVENTORY_LOCATIONS = Object.freeze({
  NY: Object.freeze({
    locationCode: 'NY',
    netsuiteInternalId: WarehouseNetsuiteIds[Warehouses.NEW_YORK],
  }),
  CO: Object.freeze({
    locationCode: 'CO',
    netsuiteInternalId: WarehouseNetsuiteIds[Warehouses.COLORADO],
  }),
})

/** Lot is all-CO or all-NY — no "Any". Default CO (ops site for this interface). */
export const DEFAULT_LOT_INVENTORY_LOCATION = LOT_INVENTORY_LOCATIONS.CO

/** v-select options stay code-valued; normalize to rich shape on input. */
export const LOT_INVENTORY_LOCATION_OPTIONS = Object.freeze([
  {text: 'CO', value: 'CO'},
  {text: 'NY', value: 'NY'},
])

/** Browse filter for full-catalog search — includes Any. Lot governor stays CO|NY only. */
export const CATALOG_INVENTORY_LOCATION_OPTIONS = Object.freeze([
  {text: 'Any inventory', value: null},
  {text: 'CO inventory', value: 'CO'},
  {text: 'NY inventory', value: 'NY'},
])

/** Synthetic location ids for prototype on-hand splits until Product returns real warehouse FKs. */
const PROTOTYPE_LOC = Object.freeze({
  NY: Object.freeze({
    locationId: 'prototype-loc-ny',
    ...LOT_INVENTORY_LOCATIONS.NY,
  }),
  CO: Object.freeze({
    locationId: 'prototype-loc-co',
    ...LOT_INVENTORY_LOCATIONS.CO,
  }),
})

export function normalizeInventoryLocation(value) {
  const code = String(value || '').trim().toUpperCase()
  return code || null
}

/**
 * Plumbed location snapshot → `{locationCode, state, netsuiteInternalId}`.
 * Callers pass a rich object; we do not invent ids or soft-null missing ones.
 */
export function resolveInventoryLocation(location) {
  const locationCode = normalizeInventoryLocation(location.state || location.locationCode)
  return {
    locationCode,
    state: locationCode,
    netsuiteInternalId: String(location.netsuiteInternalId),
  }
}

/**
 * Lot governor: always `{locationCode, netsuiteInternalId}`.
 * String codes convert at the door via LOT_INVENTORY_LOCATIONS; empty → CO.
 * Objects are trusted to already carry netsuiteInternalId.
 */
export function normalizeLotInventoryLocation(value) {
  if (value == null || value === '') return DEFAULT_LOT_INVENTORY_LOCATION
  if (typeof value === 'object') {
    const code = normalizeInventoryLocation(value.locationCode || value.state)
    if (code !== 'NY' && code !== 'CO') {
      throw new Error(`lot inventory location must be CO or NY (got ${code})`)
    }
    return {locationCode: code, netsuiteInternalId: String(value.netsuiteInternalId)}
  }
  const code = normalizeInventoryLocation(value)
  if (code === 'NY' || code === 'CO') return LOT_INVENTORY_LOCATIONS[code]
  throw new Error(`lot inventory location must be CO or NY (got ${value})`)
}

/** Code for filters, conflict checks, and v-select binding. */
export function lotInventoryLocationCode(lotLocation) {
  return normalizeLotInventoryLocation(lotLocation).locationCode
}

/**
 * True when any catalog line was snapped from a different warehouse than the lot.
 * Used to block mid-compose lot-location flips that would mix CO + NY on one lot.
 */
export function catalogEntriesConflictWithLotLocation(entries, lotLocation) {
  const lotCode = lotInventoryLocationCode(lotLocation)
  return (entries || []).some((entry) => {
    const code = normalizeInventoryLocation(entry?.locationSnapshot?.locationCode)
    if (!code || code === 'AGG') return false
    return code !== lotCode
  })
}

/**
 * Stable 0..0.40 fraction from a string (sku/id) so mock splits don't jitter.
 * @param {string} seed
 * @returns {number}
 */
function stableUnitFraction(seed) {
  let hash = 0
  const text = String(seed || '')
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i)
    hash |= 0
  }
  return (Math.abs(hash) % 41) / 100
}

/**
 * Synthesizes a stable CO/NY on-hand split when Product only has aggregate
 * qty. Prefer real `onHandByLocation` when present.
 * @param {object} product
 * @returns {object}
 */
export function withPrototypeOnHandByLocation(product) {
  if (!product || typeof product !== 'object') return product
  const existing = Array.isArray(product.onHandByLocation) ? product.onHandByLocation : []
  const hasNamed = existing.some((row) => {
    const code = normalizeInventoryLocation(row?.locationCode)
    return code === 'CO' || code === 'NY'
  })
  if (hasNamed) return product

  const total = Number(product.qtyOnHand ?? product.qtyAvailable)
  if (!Number.isFinite(total) || total <= 0) return product

  // Whole units only — inventory is discrete pieces, not a continuous split.
  const whole = Math.floor(total)
  if (whole <= 0) return product

  const nyShare = 0.35 + stableUnitFraction(product.sku || product.id)
  const nyQty = Math.min(whole, Math.max(0, Math.round(whole * nyShare)))
  const coQty = whole - nyQty

  return {
    ...product,
    onHandByLocation: [
      {...PROTOTYPE_LOC.NY, qtyOnHand: nyQty},
      {...PROTOTYPE_LOC.CO, qtyOnHand: coQty},
    ],
  }
}

export function catalogInventoryLocations(product) {
  const rows = Array.isArray(product?.onHandByLocation) ? product.onHandByLocation : []
  if (rows.length > 0) {
    return rows.map((row) => ({
      locationId: row.locationId || null,
      locationCode: normalizeInventoryLocation(row.locationCode),
      netsuiteInternalId: row.netsuiteInternalId == null
        ? null
        : String(row.netsuiteInternalId),
      qtyOnHand: Number(row.qtyOnHand),
    }))
  }

  const qty = Number(product?.qtyOnHand ?? product?.qtyAvailable)
  return Number.isFinite(qty)
    ? [{locationId: null, locationCode: 'AGG', netsuiteInternalId: null, qtyOnHand: qty}]
    : []
}

export function inventoryQuantityForLocation(product, locationCode = null) {
  const normalizedLocation = normalizeInventoryLocation(locationCode)
  const locations = catalogInventoryLocations(product)
  if (normalizedLocation) {
    const match = locations.find((row) => row.locationCode === normalizedLocation)
    return Number.isFinite(match?.qtyOnHand) ? match.qtyOnHand : 0
  }
  return locations.reduce((sum, row) => sum + (Number.isFinite(row.qtyOnHand) ? row.qtyOnHand : 0), 0)
}

/** Stable CO/NY qty pair for display. Missing location → 0; AGG-only → nulls. */
export function coNyOnHandBreakdown(product) {
  const locations = catalogInventoryLocations(product)
  const hasNamed = locations.some((row) => row.locationCode === 'CO' || row.locationCode === 'NY')
  if (!hasNamed) {
    return {co: null, ny: null}
  }
  const qty = (code) => {
    const match = locations.find((row) => row.locationCode === code)
    return Number.isFinite(match?.qtyOnHand) ? match.qtyOnHand : 0
  }
  return {co: qty('CO'), ny: qty('NY')}
}

export function hasCatalogInventory(product, locationCode = null) {
  return inventoryQuantityForLocation(product, locationCode) > 0
}

export function pickInventoryLocation(product, locationCode = null) {
  const normalizedLocation = normalizeInventoryLocation(locationCode)
  const locations = catalogInventoryLocations(product)
  if (normalizedLocation) {
    return locations.find((row) => row.locationCode === normalizedLocation && row.qtyOnHand > 0) || null
  }
  // No filter → prefer CO (lot default), then any other site with stock.
  const co = locations.find((row) => row.locationCode === 'CO' && row.qtyOnHand > 0)
  if (co) return co
  return locations.find((row) => row.qtyOnHand > 0) || null
}

/**
 * Always show CO vs NY when known. `locationCode` still drives filter/pick
 * elsewhere — it must not collapse the on-hand breakdown.
 */
export function inventorySummaryLabel(product, _locationCode = null) {
  const locations = catalogInventoryLocations(product)
  const total = inventoryQuantityForLocation(product)
  if (locations.length === 0 || total <= 0) return 'inventory unavailable'

  const {co, ny} = coNyOnHandBreakdown(product)
  if (co == null && ny == null) {
    return `${total} on hand · CO — · NY —`
  }
  return `${total} on hand · CO ${co} · NY ${ny}`
}

export const __testing__ = Object.freeze({
  PROTOTYPE_LOC,
  stableUnitFraction,
})
